// src/pages/UserPage/sections/rideShare/RouteDialog.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Grid,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Collapse,
  Alert,
  Divider,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  alpha,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Car, X, Check } from 'lucide-react';
import { LocationAutocomplete } from '../../../../shared/components/AutoComplete/LocationAutocomplete';
import {
  EMPTY_ROUTE,
  OCCURRENCE,
  OCCURRENCE_OPTIONS,
  PRICE_FALLBACK,
} from './rideShareUtils';

const SectionLabel = ({ children }) => (
  <Typography
    variant="caption" fontWeight={600} color="text.secondary"
    sx={{ textTransform: 'uppercase', letterSpacing: 0.5, mb: 1, display: 'block' }}
  >
    {children}
  </Typography>
);

/**
 * Route detail modal — used both for "Tarkenna" (new route with details)
 * and for editing an existing route.
 *
 * Props:
 *   open, initial, isEdit, saving, error,
 *   onClose(), onSave(draft), onDismissError()
 */
export const RouteDialog = ({
  open,
  initial,
  isEdit = false,
  saving = false,
  error = null,
  onClose,
  onSave,
  onDismissError,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const [draft, setDraft] = useState(initial || EMPTY_ROUTE);

  // Re-seed whenever the dialog is (re)opened for a different route
  useEffect(() => {
    if (open) setDraft(initial || EMPTY_ROUTE);
  }, [open, initial]);

  const setField = (key) => (e) => setDraft((p) => ({ ...p, [key]: e.target.value }));

  const setOccurrence = (_, value) => {
    if (!value) return; // ignore deselect
    setDraft((p) => ({
      ...p,
      occurrence: value,
      // leaving custom clears the specifics so they can't linger invisibly
      ...(value === OCCURRENCE.CUSTOM ? {} : { date: '', time: '' }),
    }));
  };

  const isCustom = draft.occurrence === OCCURRENCE.CUSTOM;
  const canSave  = draft.origin.trim() && draft.destination.trim() && !saving;

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      fullScreen={fullScreen}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { borderRadius: fullScreen ? 0 : '16px' } }}
    >
      <DialogTitle sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: 2, pb: 1.5,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{
            width: 32, height: 32, borderRadius: '9px', flexShrink: 0,
            bgcolor: isDark ? '#2e1065' : '#f5f3ff',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            border: '1px solid', borderColor: alpha('#7c3aed', 0.2),
          }}>
            <Car size={16} color="#7c3aed" strokeWidth={1.75} />
          </Box>
          <Typography variant="subtitle1" fontWeight={700}>
            {isEdit ? 'Muokkaa reittiä' : 'Tarkenna reittiä'}
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose} disabled={saving} sx={{ borderRadius: '8px' }}>
          <X size={18} />
        </IconButton>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 2.5 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={onDismissError}>{error}</Alert>
        )}

        <Grid container spacing={2}>
          {/* ── Route ── */}
          <Grid item xs={12} sm={6}>
            <LocationAutocomplete
              label="Mistä"
              placeholder="esim. Helsinki"
              value={draft.origin}
              onChange={(val) => setDraft((p) => ({ ...p, origin: val }))}
              onLocationSelect={(loc) =>
                setDraft((p) => ({ ...p, origin: loc.city || loc.address || p.origin }))}
              showDetectLocation
              countryCode="fi"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <LocationAutocomplete
              label="Minne"
              placeholder="esim. Joensuu"
              value={draft.destination}
              onChange={(val) => setDraft((p) => ({ ...p, destination: val }))}
              onLocationSelect={(loc) =>
                setDraft((p) => ({ ...p, destination: loc.city || loc.address || p.destination }))}
              countryCode="fi"
            />
          </Grid>

          {/* ── Timing ── */}
          <Grid item xs={12}>
            <SectionLabel>Milloin ajat tätä reittiä</SectionLabel>
            <ToggleButtonGroup
              value={draft.occurrence}
              exclusive
              fullWidth
              size="small"
              onChange={setOccurrence}
              disabled={saving}
              sx={{ '& .MuiToggleButton-root': { textTransform: 'none', fontWeight: 600 } }}
            >
              {OCCURRENCE_OPTIONS.map((opt) => (
                <ToggleButton key={opt.value} value={opt.value}>{opt.label}</ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Grid>

          <Grid item xs={12} sx={{ pt: '0 !important' }}>
            <Collapse in={isCustom} unmountOnExit>
              <Grid container spacing={2} sx={{ pt: 2 }}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Päivämäärä" type="date" fullWidth size="small"
                    value={draft.date}
                    onChange={setField('date')}
                    disabled={saving}
                    InputLabelProps={{ shrink: true }}
                    helperText={draft.date ? ' ' : 'Valitse päivä'}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Kellonaika" type="time" fullWidth size="small"
                    value={draft.time}
                    onChange={setField('time')}
                    disabled={saving || !draft.date}
                    InputLabelProps={{ shrink: true }}
                    helperText={draft.date ? 'Valinnainen' : 'Aseta ensin päivämäärä'}
                  />
                </Grid>
              </Grid>
            </Collapse>
          </Grid>

          {/* ── Price ── */}
          <Grid item xs={12} sm={6}>
            <SectionLabel>Hinta</SectionLabel>
            <TextField
              label="Hinta (€)" fullWidth size="small"
              value={draft.price}
              onChange={setField('price')}
              disabled={saving}
              inputMode="decimal"
              placeholder="—"
              helperText={draft.price ? 'Per kyytiläinen' : PRICE_FALLBACK}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button
          variant="outlined" onClick={onClose} disabled={saving}
          sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 600 }}
        >
          Peruuta
        </Button>
        <Button
          variant="contained"
          onClick={() => onSave(draft)}
          disabled={!canSave}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <Check size={16} />}
          sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 700, boxShadow: 'none',
                '&:hover': { boxShadow: 'none' } }}
        >
          {isEdit ? 'Tallenna' : 'Luo reitti'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RouteDialog;